
import {
  defineComponent,
  ref,
  Ref,
  h,
  withDirectives,
  computed,
  watch,
  PropType,
  CSSProperties,
} from 'vue';
import visibilityDirective from '@/helpers/directives/visibility';

// one transparent pixel
const PLUG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

export default defineComponent({
  name: 'AcreImage',
  props: {
    src: {
      type: String as PropType<string>,
      required: true,
    },
    bg: Boolean,
    force: Boolean,
    skipPreload: Boolean,
    width: [Number, String],
    height: [Number, String],
    placeholder: Boolean,
    placeholderSrc: String,
    placeholderDisabled: Boolean,
    visibilityTopMargin: {
      type: String,
      default: '0px',
    },
  },
  setup(props) {
    const isLoaded: Ref<boolean> = ref(props.force);
    const isReadyToPreload: Ref<boolean> = ref(props.force);

    const imgAspectRation = computed(() => {
      if (props.width && props.height) {
        return Number(props.height) / Number(props.width);
      }
      return null;
    });

    const mainImgStyle = computed(() => {
      const style: CSSProperties = {};
      if (!isLoaded.value) style.opacity = 0;
      if (props.bg && isLoaded.value) {
        style.backgroundImage = `url(${props.src})`;
      }
      if (imgAspectRation.value) style.position = 'absolute';
      return style;
    });

    const placeholderStyle = computed(() => {
      const style: CSSProperties = {};
      if (props.bg && props.placeholderSrc) style.backgroundImage = `url(${props.placeholderSrc})`;
      if (isLoaded.value) style.visibility = 'hidden';
      return style;
    });

    const containerStyle = computed(() => {
      const style: CSSProperties = {};
      if (imgAspectRation.value) style.paddingTop = `${imgAspectRation.value * 100}%`;
      return style;
    });

    const readyImgSrc = computed(() => (isLoaded.value ? props.src : PLUG));

    function onVisible() {
      if (window.__prerender) return;
      isReadyToPreload.value = true;
    }

    function init() {
      isReadyToPreload.value = props.force;
      isLoaded.value = props.force;
    }

    function preloadImage() {
      if (!isReadyToPreload.value) return;
      if (props.skipPreload) {
        isLoaded.value = true;
        return;
      }
      const img = new Image();
      img.src = props.src;
      img.addEventListener('load', () => {
        isLoaded.value = true;
      });
    }

    watch(() => props.src, init);
    watch(isReadyToPreload, preloadImage);

    return {
      isLoaded,
      imgAspectRation,
      mainImgStyle,
      placeholderStyle,
      containerStyle,
      readyImgSrc,
      onVisible,
      init,
    };
  },
  render() {
    const { class: inheritClass, ...attrs } = this.$attrs;
    const imageTag: string = this.bg ? 'div' : 'img';
    const isPlaceholder: boolean = !this.placeholderDisabled && Boolean(this.placeholder || this.placeholderSrc);
    const mainImageEl = h(
      imageTag,
      {
        class: [inheritClass, isPlaceholder ? 'acre-image__main' : 'acre-image'],
        style: this.mainImgStyle,
        src: this.bg ? undefined : this.readyImgSrc,
        draggable: false,
        ref: 'img',
        key: this.src,
        ...attrs,
      },
      [this.$slots?.default?.()],
    );
    const mainImageElWithVisibility = withDirectives(mainImageEl, [
      [visibilityDirective, this.onVisible, this.visibilityTopMargin],
    ]);
    if (!isPlaceholder) return mainImageElWithVisibility;
    const placeholderImgEl = h(imageTag, {
      class: 'acre-image__placeholder',
      style: this.placeholderStyle,
      src: this.bg ? undefined : this.placeholderSrc,
      ...attrs,
    });
    return h(
      'div',
      {
        style: this.containerStyle,
        class: ['acre-image acre-image--placeholder', { 'acre-image--loading': !this.isLoaded }],
      },
      [placeholderImgEl, mainImageElWithVisibility],
    );
  },
});
